import { z } from "zod";
import { dateOnlySchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { listResponseSchema, timestampsShape } from "./common.ts";
import { DEFAULT_GST_RATE_BPS } from "./india.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * CATALOG — the service catalog, packages, tax rates and rate cards.
 *
 * This is the price list the whole product reads from: leads size an
 * opportunity with it, proposals quote from it, engagements bill from it and
 * invoice lines are built out of it (PRD US-F4.7, §5.1 SAC defaults).
 *
 * Two India-first rules are baked in:
 *  - every service carries a SAC code and a GST rate in basis points, because a
 *    GST invoice line needs both (PRD §5.2);
 *  - prices per currency are EXPLICIT (US-17.7): a service priced in INR has no
 *    USD price until somebody sets one. Nothing here silently converts money.
 *
 * Monetary fields are `null` for callers without `financials:view` — the same
 * masking the members API applies to cost/bill rates.
 */

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/** How a service is sold. Drives the quantity label on proposals and invoices. */
export const SERVICE_UNITS = ["month", "hour", "piece", "project", "day", "word", "post"] as const;
export type ServiceUnit = (typeof SERVICE_UNITS)[number];
export const serviceUnitSchema = z.enum(SERVICE_UNITS);

export const SERVICE_UNIT_LABELS: Record<ServiceUnit, { singular: string; plural: string; short: string }> = {
  month: { singular: "month", plural: "months", short: "mo" },
  hour: { singular: "hour", plural: "hours", short: "hr" },
  piece: { singular: "piece", plural: "pieces", short: "pc" },
  project: { singular: "project", plural: "projects", short: "proj" },
  day: { singular: "day", plural: "days", short: "day" },
  word: { singular: "word", plural: "words", short: "word" },
  post: { singular: "post", plural: "posts", short: "post" },
};

/**
 * Price per currency, keyed by ISO-4217 code. RESPONSE side: a plain map, so a
 * row's stored JSON encodes without surprises. `null` = the caller may not see
 * money. An absent currency means "not priced in that currency" — never
 * "convert it for me".
 */
export const priceMapSchema = z.record(z.string(), minorSchema);
export type PriceMap = z.infer<typeof priceMapSchema>;

/** INPUT side: only enabled ISO-4217 codes, non-negative amounts. Replaced as a whole on PATCH. */
export const priceMapInputSchema = z.partialRecord(currencyCodeSchema, minorSchema.min(0));
export type PriceMapInput = z.input<typeof priceMapInputSchema>;

/** GST rate in basis points (1800 = 18%). Rates are data, not code (PRD §5.1). */
export const taxRateBpsSchema = z.number().int().min(0).max(10_000);

/** Quantity with up to 3 decimals (stored as integer thousandths — never a float). */
export const quantitySchema = z.number().positive().max(1_000_000);

export const CATALOG_MAX_PACKAGE_ITEMS = 100;

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------

/** A billable service in the catalog (retainer line, deliverable, hourly rate). */
export const serviceSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** Short internal code, unique per workspace (e.g. "SMM-RET"). */
  code: z.string().nullable(),
  /** 6-digit SAC code printed on the GST invoice line. */
  sacCode: z.string().nullable(),
  unit: serviceUnitSchema,
  category: z.string().nullable(),
  /** Headline price in `currency`. `null` without `financials:view`. */
  defaultPriceMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  /** Explicit price per currency, including `currency` itself. `null` without `financials:view`. */
  prices: priceMapSchema.nullable(),
  /** What delivering one unit costs the agency (for margin). `null` without `financials:view`. */
  costBasisMinor: minorSchema.nullable(),
  taxRateBps: z.number().int(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ServiceDto = z.infer<typeof serviceSchema>;

/** Writable shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const serviceWritableShape = {
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullable(),
  code: z.string().trim().max(40).nullable(),
  sacCode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Use a 6-digit SAC code")
    .nullable(),
  unit: serviceUnitSchema,
  category: z.string().trim().max(80).nullable(),
  defaultPriceMinor: minorSchema.min(0),
  currency: currencyCodeSchema,
  prices: priceMapInputSchema,
  costBasisMinor: minorSchema.min(0).nullable(),
  taxRateBps: taxRateBpsSchema,
  isActive: z.boolean(),
  sortOrder: z.number().int().min(0).max(100_000),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/services (perm `clients:write`) → 201 Service.
 * Duplicate `code` → 409 CONFLICT. A price in a currency the workspace has not
 * enabled → 422.
 */
export const createServiceInputSchema = z
  .object({
    ...serviceWritableShape,
    description: serviceWritableShape.description.optional(),
    code: serviceWritableShape.code.optional(),
    sacCode: serviceWritableShape.sacCode.optional(),
    unit: serviceWritableShape.unit.optional(),
    category: serviceWritableShape.category.optional(),
    defaultPriceMinor: serviceWritableShape.defaultPriceMinor.optional(),
    currency: serviceWritableShape.currency.optional(),
    prices: serviceWritableShape.prices.optional(),
    costBasisMinor: serviceWritableShape.costBasisMinor.optional(),
    taxRateBps: serviceWritableShape.taxRateBps.optional(),
    isActive: serviceWritableShape.isActive.optional(),
    sortOrder: serviceWritableShape.sortOrder.optional(),
    tags: serviceWritableShape.tags.optional(),
    customFields: serviceWritableShape.customFields.optional(),
  })
  .strict();
export type CreateServiceInput = z.input<typeof createServiceInputSchema>;

/** PATCH /api/v1/services/:id (perm `clients:write`) → Service. Merge patch; `prices` is replaced as a whole. */
export const updateServiceInputSchema = z.object(serviceWritableShape).partial().strict();
export type UpdateServiceInput = z.input<typeof updateServiceInputSchema>;

/** POST /api/v1/services/:id/duplicate (perm `clients:write`) → 201 Service ("<name> (copy)" unless named). */
export const duplicateServiceInputSchema = z
  .object({ name: z.string().trim().min(1).max(200).optional() })
  .strict();
export type DuplicateServiceInput = z.input<typeof duplicateServiceInputSchema>;

/** GET /api/v1/services (any member) → page of Service, archived rows excluded unless asked for. */
export const listServicesQuerySchema = listQuerySchema.extend({
  active: z.enum(["true", "false"]).optional(),
  includeArchived: z.enum(["true", "false"]).optional(),
  category: z.string().trim().max(80).optional(),
  unit: serviceUnitSchema.optional(),
  sort: z.enum(["name", "sortOrder", "defaultPriceMinor", "createdAt", "updatedAt"]).optional(),
});
export type ListServicesQuery = z.infer<typeof listServicesQuerySchema>;

// ---------------------------------------------------------------------------
// Packages
// ---------------------------------------------------------------------------

/** One service inside a package, with its quantity and optional price override. */
export const packageItemSchema = z.object({
  id: uuidSchema,
  serviceId: uuidSchema,
  serviceName: z.string(),
  /** Up to 3 decimals. */
  quantity: z.number(),
  /** Overrides the service's price for this package. `null` = use the service price (or masked). */
  unitPriceMinor: minorSchema.nullable(),
  sortOrder: z.number().int(),
});
export type PackageItemDto = z.infer<typeof packageItemSchema>;

/** A sellable bundle: services with quantities, a headline price and deliverable quotas. */
export const packageSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  code: z.string().nullable(),
  /** Headline price in `currency`. `null` without `financials:view`. */
  priceMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  prices: priceMapSchema.nullable(),
  /** Deliverable quotas per period, e.g. `{ "reels": 4, "posts": 12 }`. */
  quotas: z.record(z.string(), z.number()),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  tags: z.array(z.string()),
  items: z.array(packageItemSchema),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type PackageDto = z.infer<typeof packageSchema>;

export const packageItemInputSchema = z
  .object({
    serviceId: uuidSchema,
    quantity: quantitySchema,
    unitPriceMinor: minorSchema.min(0).nullable().optional(),
  })
  .strict();
export type PackageItemInput = z.input<typeof packageItemInputSchema>;

const packageWritableShape = {
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullable(),
  code: z.string().trim().max(40).nullable(),
  priceMinor: minorSchema.min(0),
  currency: currencyCodeSchema,
  prices: priceMapInputSchema,
  quotas: z.record(z.string().trim().min(1).max(60), z.number().int().min(0).max(100_000)),
  isActive: z.boolean(),
  sortOrder: z.number().int().min(0).max(100_000),
  tags: tagsSchema,
  /** Replaced as a whole: the list you send becomes the package's contents. */
  items: z.array(packageItemInputSchema).max(CATALOG_MAX_PACKAGE_ITEMS),
};

/** POST /api/v1/packages (perm `clients:write`) → 201 Package. Unknown `serviceId` → 422. */
export const createPackageInputSchema = z
  .object({
    ...packageWritableShape,
    description: packageWritableShape.description.optional(),
    code: packageWritableShape.code.optional(),
    priceMinor: packageWritableShape.priceMinor.optional(),
    currency: packageWritableShape.currency.optional(),
    prices: packageWritableShape.prices.optional(),
    quotas: packageWritableShape.quotas.optional(),
    isActive: packageWritableShape.isActive.optional(),
    sortOrder: packageWritableShape.sortOrder.optional(),
    tags: packageWritableShape.tags.optional(),
    items: packageWritableShape.items.optional(),
  })
  .strict();
export type CreatePackageInput = z.input<typeof createPackageInputSchema>;

/** PATCH /api/v1/packages/:id (perm `clients:write`) → Package. `items`, `prices` and `quotas` are replaced as a whole. */
export const updatePackageInputSchema = z.object(packageWritableShape).partial().strict();
export type UpdatePackageInput = z.input<typeof updatePackageInputSchema>;

/** GET /api/v1/packages (any member) → page of Package. */
export const listPackagesQuerySchema = listQuerySchema.extend({
  active: z.enum(["true", "false"]).optional(),
  includeArchived: z.enum(["true", "false"]).optional(),
  sort: z.enum(["name", "sortOrder", "priceMinor", "createdAt", "updatedAt"]).optional(),
});
export type ListPackagesQuery = z.infer<typeof listPackagesQuerySchema>;

// ---------------------------------------------------------------------------
// Tax rates
// ---------------------------------------------------------------------------

export const TAX_RATE_APPLIES_TO = ["services", "goods", "all"] as const;
export type TaxRateAppliesTo = (typeof TAX_RATE_APPLIES_TO)[number];
export const taxRateAppliesToSchema = z.enum(TAX_RATE_APPLIES_TO);

/** A named GST rate the workspace bills with. Seeded on signup, fully editable (PRD §5.1). */
export const taxRateSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  rateBps: z.number().int(),
  appliesTo: taxRateAppliesToSchema,
  /** Exactly one rate per workspace is the default for new services and invoice lines. */
  isDefault: z.boolean(),
  /** Exempt supply (§5.2): 0% is charged, but it is not a zero-rated supply. */
  isExempt: z.boolean(),
  isActive: z.boolean(),
  description: z.string().nullable(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type TaxRateDto = z.infer<typeof taxRateSchema>;

const taxRateWritableShape = {
  name: z.string().trim().min(1).max(100),
  rateBps: taxRateBpsSchema,
  appliesTo: taxRateAppliesToSchema,
  isDefault: z.boolean(),
  isExempt: z.boolean(),
  isActive: z.boolean(),
  description: z.string().trim().max(500).nullable(),
};

/** POST /api/v1/tax-rates (perm `org:manage`) → 201 TaxRate. Duplicate name → 409. */
export const createTaxRateInputSchema = z
  .object({
    ...taxRateWritableShape,
    appliesTo: taxRateWritableShape.appliesTo.optional(),
    isDefault: taxRateWritableShape.isDefault.optional(),
    isExempt: taxRateWritableShape.isExempt.optional(),
    isActive: taxRateWritableShape.isActive.optional(),
    description: taxRateWritableShape.description.optional(),
  })
  .strict();
export type CreateTaxRateInput = z.input<typeof createTaxRateInputSchema>;

/** PATCH /api/v1/tax-rates/:id (perm `org:manage`) → TaxRate. Clearing the last default → 409. */
export const updateTaxRateInputSchema = z.object(taxRateWritableShape).partial().strict();
export type UpdateTaxRateInput = z.input<typeof updateTaxRateInputSchema>;

/** GET /api/v1/tax-rates (any member) → the workspace's rates, default first. */
export const listTaxRatesQuerySchema = z
  .object({
    active: z.enum(["true", "false"]).optional(),
    includeArchived: z.enum(["true", "false"]).optional(),
  })
  .strict();
export type ListTaxRatesQuery = z.infer<typeof listTaxRatesQuerySchema>;

export const taxRateListSchema = listResponseSchema(taxRateSchema);

// ---------------------------------------------------------------------------
// Rate cards
// ---------------------------------------------------------------------------

/**
 * One effective-dated bill rate. It applies to a ROLE (`roleKey`) or to one
 * PERSON (`userId`) — exactly one of the two, enforced by a CHECK constraint.
 * `effectiveFrom`/`effectiveTo` are inclusive calendar dates in the workspace
 * timezone; `effectiveTo: null` means "still current".
 */
export const rateCardItemSchema = z.object({
  id: uuidSchema,
  rateCardId: uuidSchema,
  roleKey: z.string().nullable(),
  userId: uuidSchema.nullable(),
  /** `null` without `financials:view`. */
  rateMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  effectiveFrom: dateOnlySchema,
  effectiveTo: dateOnlySchema.nullable(),
  note: z.string().nullable(),
  ...timestampsShape,
});
export type RateCardItemDto = z.infer<typeof rateCardItemSchema>;

/** A set of bill rates used by time tracking and profitability. */
export const rateCardSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  currency: currencyCodeSchema,
  isDefault: z.boolean(),
  isActive: z.boolean(),
  items: z.array(rateCardItemSchema),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type RateCardDto = z.infer<typeof rateCardSchema>;

const rateCardWritableShape = {
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullable(),
  currency: currencyCodeSchema,
  isDefault: z.boolean(),
  isActive: z.boolean(),
};

/** POST /api/v1/rate-cards (perm `org:manage`) → 201 RateCard. Duplicate name → 409. */
export const createRateCardInputSchema = z
  .object({
    ...rateCardWritableShape,
    description: rateCardWritableShape.description.optional(),
    currency: rateCardWritableShape.currency.optional(),
    isDefault: rateCardWritableShape.isDefault.optional(),
    isActive: rateCardWritableShape.isActive.optional(),
  })
  .strict();
export type CreateRateCardInput = z.input<typeof createRateCardInputSchema>;

/** PATCH /api/v1/rate-cards/:id (perm `org:manage`) → RateCard. */
export const updateRateCardInputSchema = z.object(rateCardWritableShape).partial().strict();
export type UpdateRateCardInput = z.input<typeof updateRateCardInputSchema>;

const rateCardItemWritableShape = {
  roleKey: z.string().trim().min(1).max(60).nullable(),
  userId: uuidSchema.nullable(),
  rateMinor: minorSchema.min(0),
  currency: currencyCodeSchema,
  effectiveFrom: dateOnlySchema,
  effectiveTo: dateOnlySchema.nullable(),
  note: z.string().trim().max(500).nullable(),
};

/**
 * POST /api/v1/rate-cards/:id/items (perm `org:manage`) → 201 RateCardItem.
 * Exactly one of `roleKey` / `userId` → 422; an overlapping period for the same
 * subject → 409.
 */
export const createRateCardItemInputSchema = z
  .object({
    ...rateCardItemWritableShape,
    roleKey: rateCardItemWritableShape.roleKey.optional(),
    userId: rateCardItemWritableShape.userId.optional(),
    currency: rateCardItemWritableShape.currency.optional(),
    effectiveTo: rateCardItemWritableShape.effectiveTo.optional(),
    note: rateCardItemWritableShape.note.optional(),
  })
  .strict();
export type CreateRateCardItemInput = z.input<typeof createRateCardItemInputSchema>;

/** PATCH /api/v1/rate-cards/:id/items/:itemId (perm `org:manage`) → RateCardItem. */
export const updateRateCardItemInputSchema = z.object(rateCardItemWritableShape).partial().strict();
export type UpdateRateCardItemInput = z.input<typeof updateRateCardItemInputSchema>;

export const rateCardItemParamsSchema = z.object({ id: uuidSchema, itemId: uuidSchema });
export type RateCardItemParams = z.infer<typeof rateCardItemParamsSchema>;

/** GET /api/v1/rate-cards (perm `financials:view`) → the workspace's rate cards with their items. */
export const listRateCardsQuerySchema = z
  .object({
    active: z.enum(["true", "false"]).optional(),
    includeArchived: z.enum(["true", "false"]).optional(),
  })
  .strict();
export type ListRateCardsQuery = z.infer<typeof listRateCardsQuerySchema>;

export const rateCardListSchema = listResponseSchema(rateCardSchema);

/** What `resolveRate()` answers with: the rate in force for a person on a date. */
export const resolvedRateSchema = z.object({
  rateCardId: uuidSchema,
  itemId: uuidSchema,
  rateMinor: minorSchema,
  currency: currencyCodeSchema,
  /** `user` beats `role` when both match. */
  source: z.enum(["user", "role"]),
});
export type ResolvedRate = z.infer<typeof resolvedRateSchema>;

// ---------------------------------------------------------------------------
// SAC reference
// ---------------------------------------------------------------------------

export const sacCodeSchema = z.object({ code: z.string(), description: z.string() });
export type SacCodeDto = z.infer<typeof sacCodeSchema>;

/** GET /api/v1/catalog/sac-codes?q= (any member) → the agency SAC reference, filterable. */
export const listSacCodesQuerySchema = z.object({ q: z.string().trim().max(100).optional() }).strict();
export type ListSacCodesQuery = z.infer<typeof listSacCodesQuerySchema>;

export const sacCodeListSchema = listResponseSchema(sacCodeSchema);

/** The GST rate a service falls back to when nothing else says otherwise (18%). */
export const CATALOG_DEFAULT_TAX_RATE_BPS = DEFAULT_GST_RATE_BPS;
